import type {ReactNode} from 'react';
import {Search, Sparkles} from 'lucide-react';
import {useIntl} from 'react-intl';
import {SkeletonBlock} from 'ch3-ui-lib';
import type {Call, CallSummary, InsightTopic, NextStepOwner} from '../../../../store/features/calls/callsService';
import {formatTime} from '../../../../utilis/timeUtilis';
import {insightsMessages as m} from '../insightsMessages';
import {bodyText, brandButton, focusRing, lucide, readingMeasure, sectionTitle} from '../insightsUi';
import {useInsightsActions} from '../InsightsActions';
import {isFinalError} from '../insightsErrors';
import {Citations} from './CitationChip';
import {ContactSuggestionCard, type ContactPanelRequest} from './ContactSuggestionCard';
import {FollowUpComposer} from './FollowUpComposer';
import type {FollowUpDrafts} from './useFollowUpDrafts';
import {GenerationStatus, type GenerationState} from './GenerationStatus';
import {InsightChips} from './InsightChips';
import {LegacySummary} from './LegacySummary';
import {MeetingCard} from './MeetingCard';
import {clientShortName} from '../transcript/speakers';

export interface SummaryAvailability {
    canGenerate: boolean;
    reason: string | null;
}

interface SummaryViewProps {
    call: Call;
    summary: CallSummary | undefined;
    loading: boolean;
    availability: SummaryAvailability;
    generation: GenerationState;
    onGenerate: (mode: 'generate' | 'upgrade') => void;
    onOpenContact: (request: ContactPanelRequest) => void;
    followUp: FollowUpDrafts;
}

const SummarySkeleton = () => (
    <div role="status" aria-busy="true" className="flex flex-col gap-3">
        <SkeletonBlock className="h-4 w-3/4"/>
        <SkeletonBlock className="h-3 w-1/2"/>
        <SkeletonBlock className="h-3 w-2/3"/>
        <SkeletonBlock className="h-3 w-3/5"/>
    </div>
);

const TopicChip = ({topic}: {topic: InsightTopic}) => {
    const intl = useIntl();
    const {openTopic} = useInsightsActions();
    const cited = topic.citations?.[0];
    const label = cited
        ? intl.formatMessage(m.topicJump, {topic: topic.label, time: formatTime(cited.startTime)})
        : intl.formatMessage(m.topicSearch, {topic: topic.label});
    return (
        <button
            type="button"
            onClick={() => openTopic(topic)}
            aria-label={label}
            title={label}
            className={`inline-flex h-[26px] cursor-pointer items-center gap-1 rounded-chip border border-solid border-line bg-surface px-2.5 text-footnote font-medium text-ink hover:border-brand-disabled hover:bg-brand-tint-subtle ${focusRing}`}
        >
            <Search size={12} aria-hidden="true" className={`${lucide} text-ink-muted`}/>
            {topic.label}
        </button>
    );
};

const Section = ({title, children}: {title: string; children: ReactNode}) => (
    <section className="flex flex-col gap-2.5">
        <h3 className={sectionTitle}>{title}</h3>
        {children}
    </section>
);

export const SummaryView = ({call, summary, loading, availability, generation, onGenerate, onOpenContact, followUp}: SummaryViewProps) => {
    const intl = useIntl();

    if (loading && !summary) return <SummarySkeleton/>;

    if (!summary) {
        return (
            <div className="flex flex-col items-start gap-3 rounded-card bg-brand-tint-subtle p-5" data-testid="insights-summary-empty">
                <p className="m-0 text-callout text-ink">{intl.formatMessage(m.summaryEmpty)}</p>
                {availability.canGenerate && !isFinalError(generation.error) ? (
                    generation.pending ? (
                        <GenerationStatus generation={generation}/>
                    ) : (
                        <button type="button" onClick={() => onGenerate('generate')} className={brandButton}>
                            <Sparkles size={16} aria-hidden="true" className={lucide}/>
                            {intl.formatMessage(generation.error ? m.tryAgain : m.summaryGenerate)}
                        </button>
                    )
                ) : (
                    availability.reason && !generation.error && <p className="m-0 text-footnote text-ink-muted">{availability.reason}</p>
                )}
                {!generation.pending && generation.error ? <GenerationStatus generation={generation}/> : null}
            </div>
        );
    }

    const insights = summary.insights;
    if (!insights) {
        return (
            <LegacySummary
                text={summary.summary}
                canUpgrade={availability.canGenerate}
                generation={generation}
                onUpgrade={() => onGenerate('upgrade')}
            />
        );
    }

    const keyPoints = insights.keyPoints ?? [];
    const nextSteps = insights.nextSteps ?? [];
    const topics = insights.topics ?? [];
    const meetings = insights.meetings ?? [];
    const contactEmail = call.primaryContact?.contactEmail || insights.contactDetails?.email || null;
    const ownerNames: Partial<Record<NextStepOwner, string>> = {
        AGENT: intl.formatMessage(m.ownerAgent),
        CLIENT: clientShortName(call, intl.formatMessage(m.clientFallback)),
        BOTH: intl.formatMessage(m.ownerBoth),
    };

    return (
        <div className="flex flex-col gap-6" data-testid="insights-summary">
            <div className="flex flex-col gap-3">
                {insights.headline && <p className={`m-0 ${readingMeasure} text-body-strong text-ink`}>{insights.headline}</p>}
                <InsightChips
                    reason={insights.reason}
                    outcome={insights.outcome}
                    sentiment={insights.sentiment?.label}
                    rationale={insights.sentiment?.rationale}
                />
            </div>
            {keyPoints.length > 0 && (
                <Section title={intl.formatMessage(m.keyPoints)}>
                    <ul className="m-0 flex list-disc flex-col gap-2 pl-5">
                        {keyPoints.map((point, index) => (
                            <li key={index} className={`${bodyText} text-ink`}>
                                {point.text}
                                <Citations citations={point.citations}/>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}
            {nextSteps.length > 0 && (
                <Section title={intl.formatMessage(m.nextSteps)}>
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                        {nextSteps.map((step, index) => {
                            const owner = step.owner ? ownerNames[step.owner] : undefined;
                            return (
                                <li key={index} className="flex items-start gap-2.5">
                                    <span aria-hidden="true" className="mt-[7px] size-1.5 shrink-0 rounded-full bg-brand"/>
                                    <span className={`${bodyText} text-ink`}>
                                        {owner && <span className="mr-1.5 inline-flex h-5 items-center rounded-badge bg-brand-tint px-1.5 align-[1px] text-micro text-brand-ink">{owner}</span>}
                                        {step.text}
                                        <Citations citations={step.citations}/>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </Section>
            )}
            {topics.length > 0 && (
                <Section title={intl.formatMessage(m.topics)}>
                    <div className="flex flex-wrap gap-1.5">
                        {topics.map((topic) => <TopicChip key={topic.label} topic={topic}/>)}
                    </div>
                </Section>
            )}
            {meetings.length > 0 && (
                <Section title={intl.formatMessage(m.meetings)}>
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                        {meetings.map((meeting, index) => (
                            <MeetingCard key={index} meeting={meeting} index={index} callId={call.callId} callStartedAt={call.startedAt}/>
                        ))}
                    </ul>
                </Section>
            )}
            {insights.contactDetails && (
                <ContactSuggestionCard call={call} details={insights.contactDetails} onOpen={onOpenContact}/>
            )}
            <FollowUpComposer drafts={followUp} email={contactEmail}/>
            <p className="m-0 text-footnote text-ink-muted">{intl.formatMessage(m.aiDisclaimer)}</p>
        </div>
    );
};
